/**
 * ComposeEmailPanel — shared email composer used by both the Lead Focus
 * page (Email tab) and the Sales Dialer disposition modal (Step 4).
 *
 * Iter 210.14 (Feb 2026) — extracted verbatim from
 * `LeadFocusTabs.jsx :: ComposeEmailForm` so the dialer's Step 4
 * composer no longer misses the template dropdown, OAuth auto-handler,
 * 8 MB / 10 MB pre-flight guards, HTML-escape, Resources picker
 * slide-in and the polished error messaging. Both surfaces now share
 * one implementation so any future change lands in both places.
 *
 * Behavior parity with the LeadFocusTabs original is intentional —
 * data-testids (`email-template-select`, `email-to`, `email-subject`,
 * `email-body`, `email-ai-draft`, `email-attach-btn`,
 * `email-attach-resources-btn`, `email-send-btn`, …) are preserved so
 * the existing e2e suite keeps passing.
 *
 * New props over the original:
 *   • `aiDraftEndpoint`     — default `${API}/ai/draft-email-reply`;
 *                             dialer passes `${API}/dialer/follow-up-email`
 *                             so the AI has transcript + disposition context
 *   • `extraDraftContext`   — dialer passes `{transcript, disposition}` so
 *                             the transcript-aware endpoint has what it needs
 *   • `variant`             — 'lead-focus' (default) | 'inline-dialer'
 *                             (tighter padding, no outer card border since
 *                             it's already rendered inside the modal's Step
 *                             4 section)
 *   • `initialAttachments`  — dialer pre-populates from the audit chips
 *                             the rep may have already added at the top of
 *                             Step 4
 *   • `onAttachmentsChange` — mirrors attachment changes back to the parent
 *                             so the top ATTACHMENTS chip row stays in sync
 *                             (single source of truth)
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Loader2, Send, Sparkles } from "lucide-react";
import { useAuth, API } from "@/App";
import ResourcePickerPanel, { AttachedResourcesRow } from "@/components/ResourcePickerPanel";

// Built-in templates (Lead Focus parity — same 4 that ship out of the box).
const EMAIL_TEMPLATES = [
  { id: "intro", name: "Introduction", subject: "Introducing Enable Healthcare Solutions", body: "Hi {{name}},\n\nI'd like to introduce you to Enable Healthcare's suite of solutions designed specifically for {{specialty}} practices.\n\nOur platform includes AI-powered EHR, practice management, and revenue cycle management tools that can help streamline your operations.\n\nWould you be available for a brief 15-minute call this week?\n\nBest regards" },
  { id: "followup", name: "Follow-up", subject: "Following up on our conversation", body: "Hi {{name}},\n\nI wanted to follow up on our recent conversation about Enable Healthcare solutions for {{company}}.\n\nOur solutions are designed to address exactly these challenges.\n\nWould you like to schedule a demo?\n\nBest regards" },
  { id: "demo_invite", name: "Demo Invitation", subject: "Your personalized Enable Healthcare demo", body: "Hi {{name}},\n\nI'd love to show you how Enable Healthcare can transform {{company}}'s workflow.\n\nDuring the demo, I'll walk you through:\n- AI-powered charting that saves 2+ hours daily\n- Automated billing and claims processing\n- Patient engagement tools\n\nPlease select a time that works: [calendar link]\n\nBest regards" },
  { id: "quote_followup", name: "Quote Follow-up", subject: "Your Enable Healthcare quote — next steps", body: "Hi {{name}},\n\nI wanted to check in regarding the proposal we sent for {{company}}.\n\nThe quote includes our recommended modules at a competitive rate for your practice size. I'm happy to walk through any questions.\n\nShall we schedule a quick call?\n\nBest regards" },
];

export default function ComposeEmailPanel({
  lead,
  onClose,
  onSent,
  logActivity,
  prefillTo,
  prefillSubject,
  prefillBody,
  replyTo,
  replyContext,
  aiDraftEndpoint,
  extraDraftContext,
  variant = "lead-focus",
  initialAttachments,
  onAttachmentsChange,
}) {
  const { token } = useAuth();
  const [tplId, setTplId] = useState("");
  const [to, setTo] = useState(prefillTo || lead?.contacts?.[0]?.email || lead?.email || "");
  const [subject, setSubject] = useState(prefillSubject || "");
  const [body, setBody] = useState(prefillBody || "");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiAutoTriggered, setAiAutoTriggered] = useState(false);
  const [tone, setTone] = useState("warm");
  const [sending, setSending] = useState(false);
  // Iter 170.8 (Lisa 2026-06-05) — fetch user-authored & published
  // email templates so they appear in the dropdown alongside the
  // built-ins. Was just showing the hard-coded 4.
  const [userTemplates, setUserTemplates] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/templates?type=email&status=published`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const rows = Array.isArray(r.data) ? r.data : [];
        // Normalize to the {id, name, subject, body} shape used here.
        setUserTemplates(rows.map((t) => ({
          id: `user:${t.id}`,
          name: t.name || t.title || "Untitled",
          subject: t.subject || "",
          body: t.body || t.content || "",
        })));
      } catch (_e) { /* defensive — non-fatal */ }
    })();
  }, [token]);
  // Built-ins + user templates, with a separator label in the dropdown.
  const ALL_TEMPLATES = [...EMAIL_TEMPLATES, ...userTemplates];
  const interp = (text) => (text || "")
    .replace(/{{name}}/g, lead?.contacts?.[0]?.name?.split(" ").slice(0, 2).join(" ") || "Doctor")
    .replace(/{{company}}/g, lead?.clinic || lead?.company || "your practice")
    .replace(/{{specialty}}/g, lead?.nppes?.specialty_group || lead?.specialty || "your");
  const applyEmailTemplate = (id) => {
    const t = ALL_TEMPLATES.find((x) => x.id === id);
    if (!t) return;
    setTplId(id);
    setSubject(interp(t.subject));
    setBody(interp(t.body));
    toast.success(`Loaded "${t.name}" template`);
  };

  // AI draft — context-aware. When in reply mode, uses the original email
  // content to produce a tailored reply. Otherwise produces a cold-outbound
  // draft (or, when the caller supplies a transcript-aware endpoint like the
  // dialer's `/dialer/follow-up-email`, a call-context draft).
  const aiDraft = useCallback(async (toneOverride) => {
    setAiBusy(true);
    try {
      if (replyTo && replyContext) {
        // Threaded reply draft
        const resp = await fetch(`${API}/ai/draft-email-reply`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            original: replyContext,
            lead: {
              id: lead?.id,
              contact_name: lead?.contacts?.[0]?.name || lead?.contact_name,
              company: lead?.clinic || lead?.company,
              specialty: lead?.nppes?.specialty_group || lead?.specialty,
              stage: lead?.stage,
              sub_status: lead?.subStatus || lead?.sub_status,
            },
            tone: toneOverride || tone,
          }),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          const msg = data?.detail?.message || data?.detail || data?.message || `AI draft failed (${resp.status})`;
          toast.error(typeof msg === "string" ? msg : "AI draft failed");
          return;
        }
        if (data.body) {
          setBody(data.body);
          toast.success(`AI ${toneOverride || tone} reply drafted`);
        }
      } else if (aiDraftEndpoint) {
        // Iter 210.14 — caller-supplied AI endpoint (dialer Step 4 passes
        // `/dialer/follow-up-email` which uses the RC transcript +
        // disposition context to draft a warm, personalized follow-up).
        const draftReq = {
          ...(extraDraftContext || {}),
          lead: {
            id: lead?.id,
            contact_name: lead?.contacts?.[0]?.name || lead?.contact_name,
            company: lead?.clinic || lead?.company,
            specialty: lead?.nppes?.specialty_group || lead?.specialty,
            stage: lead?.stage,
          },
          tone: toneOverride || tone,
        };
        try {
          const r = await axios.post(aiDraftEndpoint, draftReq, {
            headers: { Authorization: `Bearer ${token}` }, timeout: 25000,
          });
          const d = r?.data || {};
          if (d.subject) setSubject(d.subject);
          if (d.body) setBody(d.body);
          toast.success("✦ AI drafted the email — review before sending");
        } catch (e) {
          toast.error(`Couldn't draft email: ${e?.response?.data?.detail || e.message || "unknown"}`);
        }
      } else {
        // Cold-outbound draft (kept local for now — could swap to a backend endpoint).
        setSubject(`Cardiology benchmarks for ${lead?.clinic || lead?.company || "your practice"}`);
        setBody(`Hi ${lead?.contacts?.[0]?.name?.split(" ").slice(0, 2).join(" ") || "Doctor"},\n\nQuick note tailored to ${lead?.clinic || lead?.company || "your practice"} — I noticed you handle around ${lead?.medicare?.beneficiaries?.toLocaleString() || "—"} Medicare beneficiaries with strong ${lead?.top_hcpcs?.[0]?.code || "92928"} volume.\n\nThree things I'd love to share in a 20-min call:\n• Coding-lift benchmarks for ${lead?.nppes?.specialty_group || "your specialty"} practices your size\n• ${lead?.ehr ? `Migration playbook from ${lead.ehr.vendor}` : "Greenfield install pathway (35-day go-live)"}\n• Pricing tier that matches your beneficiary footprint\n\nFriday 10am ET work?\n\nBest,\nDemo Admin`);
        toast.success("AI draft ready");
      }
    } catch (e) {
      toast.error(`AI draft error: ${String(e?.message || e).slice(0, 120)}`);
    } finally {
      setAiBusy(false);
    }
  }, [replyTo, replyContext, lead, token, tone, aiDraftEndpoint, extraDraftContext]);

  // Auto-trigger AI reply when the form opens in reply mode (once).
  useEffect(() => {
    if (replyTo && replyContext && !aiAutoTriggered && !body.trim().replace(/-----.*-----/s, "").trim()) {
      setAiAutoTriggered(true);
      aiDraft();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replyTo]);

  // Iter 174.8 (Danny B8 fix) — attachments parity with Communications/
  // ComposeModal. Reps can attach files (PDFs, images, etc.) before
  // sending. Files are read as base64 and forwarded to the M365 Graph
  // send-mail endpoint exactly the same way ComposeModal does it.
  const [attachments, setAttachments] = useState(initialAttachments || []); // [{name,size,content_type,data_b64, id?, url?, resource_id?, source?}]
  // Iter 210.14 — mirror attachment changes to the parent (dialer keeps
  // the top-of-Step-4 chip row in sync with what the composer is
  // sending). Lead-Focus caller can omit the callback.
  useEffect(() => {
    onAttachmentsChange?.(attachments);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachments]);
  // Wave 2 / B8 — Resources picker parity. Files attached from the CRM
  // Resources library are referenced by id only (server resolves the file
  // from /app/uploads). Videos are inlined into the body as a link block.
  const [resourceFiles, setResourceFiles] = useState([]); // [{id,name,size,content_type,url,kind}]
  const [resourcePickerOpen, setResourcePickerOpen] = useState(false);

  const onAttachFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const next = [];
    for (const f of files) {
      // Iter 177 (Priya #453a41ab Feb 2026) — tightened from 24 MB to
      // 8 MB so a single file plus body text comfortably fits under
      // the 10 MB ingress/proxy cap once base64-inflated (~33%
      // overhead). Anything bigger almost always failed with a
      // browser "Failed to fetch" mid-upload anyway.
      if (f.size > 8 * 1024 * 1024) {
        toast.error(`${f.name} is over 8 MB — share via OneDrive or a link instead.`);
        continue;
      }
      const buf = await f.arrayBuffer();
      // Iter 177.7 (Lisa #5b8ade41) — chunked byte→string conversion
      // (see IssueReporter.jsx for the rationale). Snipping-tool PNGs
      // over ~64 KB raw blow the JS arg-list limit if we use
      // `String.fromCharCode(...uint8)` directly.
      const _bytes = new Uint8Array(buf);
      let _bin = "";
      const _CHUNK = 32 * 1024;
      for (let i = 0; i < _bytes.length; i += _CHUNK) {
        _bin += String.fromCharCode.apply(
          null, _bytes.subarray(i, Math.min(i + _CHUNK, _bytes.length)),
        );
      }
      const b64 = btoa(_bin);
      next.push({ name: f.name, size: f.size, content_type: f.type || "application/octet-stream", data_b64: b64, source: "upload" });
    }
    if (next.length) setAttachments((prev) => [...prev, ...next]);
  };

  const send = async () => {
    const toList = String(to || "").split(/[,;]\s*/).map(s => s.trim()).filter(Boolean);
    if (toList.length === 0) { toast.error("Add at least one recipient"); return; }
    if (!body.trim()) { toast.error("Body cannot be empty"); return; }
    setSending(true);
    try {
      const bodyHtml = body
        .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
        .replace(/\n/g, "<br/>");
      // Iter 210.14 — attachments can be a mix of shapes now:
      //   • Local uploads carry `data_b64` (inline base64 for the m365 payload)
      //   • Library picks carry `resource_id` (backend resolves from digital_assets)
      //   • Legacy lead-file uploads (from the dialer's original Step 4) may
      //     carry only `id` — coerce to inline base64 if present.
      const inline = attachments.filter((a) => a.data_b64);
      const resourceIds = [
        ...resourceFiles.map((f) => f.id).filter(Boolean),
        ...attachments.filter((a) => a.resource_id).map((a) => a.resource_id),
      ];
      const payload = {
        to: toList,
        subject: subject || undefined,
        body_html: bodyHtml,
        lead_id: lead?.id,
        in_reply_to_message_id: replyTo || undefined,
        // Iter 174.8 — include attachments so the recipient actually
        // receives the Resources picked in the composer.
        attachments: inline.length > 0
          ? inline.map((a) => ({ name: a.name, content_type: a.content_type, data_b64: a.data_b64 }))
          : undefined,
        // Wave 2 / B8 — Resources picker: pass selected asset IDs; the
        // backend resolves them from digital_assets + /app/uploads.
        resource_ids: resourceIds.length > 0 ? resourceIds : undefined,
      };
      // Iter 177 (Priya #453a41ab Feb 2026) — pre-flight payload size
      // guard. "Failed to fetch" on email send is almost always caused
      // by the request body exceeding the K8s ingress / CDN body cap
      // (typically 10-12 MB on default configs). Base64 inflates files
      // ~33% so even a single ~9 MB raw attachment can push the JSON
      // body past the limit and the browser surfaces a generic
      // "Failed to fetch" instead of a useful 413. Catch it ourselves
      // BEFORE the network round-trip with a friendly explanation.
      const approxBytes = inline.reduce(
        (n, a) => n + (a.data_b64?.length || 0), 0,
      ) + bodyHtml.length + 4096;
      if (approxBytes > 10 * 1024 * 1024) {
        const mb = (approxBytes / 1024 / 1024).toFixed(1);
        toast.error(
          `Email is ${mb} MB — too large to send inline. Remove a ` +
          `large attachment or share via OneDrive / a link instead.`,
          { duration: 9000 },
        );
        setSending(false);
        return;
      }
      let resp;
      try {
        resp = await fetch(`${API}/integrations/m365/email/send`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify(payload),
        });
      } catch (netErr) {
        // Iter 177 (Priya #453a41ab) — distinguish network failure
        // from a server error. The browser raises "TypeError: Failed
        // to fetch" when the connection is dropped (offline, payload
        // exceeded a proxy limit, mixed-content block, CORS preflight
        // refused). Give the user something actionable instead of
        // echoing the cryptic browser message.
        // eslint-disable-next-line no-console
        console.error("[email/send] network error:", netErr);
        const isAttachy = (inline.length || 0) > 0;
        toast.error(
          isAttachy
            ? "Network error sending email — try removing the attachment, or check your connection."
            : "Network error sending email — please check your connection and try again.",
          { duration: 9000 },
        );
        setSending(false);
        return;
      }
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const detail = data?.detail || {};
        const code = detail.code || data.code;
        if (code === "m365_oauth_required") {
          // 2A — inline OAuth handoff. Open Outlook consent in a
          // popup; on success the user can retry without leaving
          // the current view (compose state is preserved).
          toast.error("Connect your Outlook to send", {
            duration: 8000,
            action: {
              label: "Connect Outlook",
              onClick: async () => {
                try {
                  const r2 = await fetch(`${API}/integrations/m365/oauth/init`, {
                    headers: { Authorization: `Bearer ${token}` },
                  });
                  const j = await r2.json();
                  if (j?.url) {
                    window.open(j.url, "m365-oauth", "width=560,height=720,left=200,top=120");
                    const onMsg = (e) => {
                      if (e.data?.type === "m365-oauth" && e.data.success) {
                        toast.success("Outlook connected — try sending again");
                        window.removeEventListener("message", onMsg);
                      }
                    };
                    window.addEventListener("message", onMsg);
                  } else {
                    toast.error("Couldn't start Outlook OAuth — open Settings → Integrations.");
                  }
                } catch {
                  toast.error("Couldn't start Outlook OAuth — open Settings → Integrations.");
                }
              },
            },
          });
        } else {
          toast.error(detail.message || data.message || `Send failed (${resp.status})`);
        }
        return;
      }
      toast.success(replyTo ? "Reply sent via Outlook" : "Email sent via Outlook");
      logActivity?.("email", `Sent email: "${subject || "(no subject)"}"`);
      onSent?.({
        id: `e${Date.now()}`,
        type: "email_sent",
        at: new Date().toISOString(),
        subject: subject || "(no subject)",
        to: toList,
        preview: body.slice(0, 280),
        from: "You",
        message_id: data.message_id || data.id || null,
      });
      onClose?.();
    } catch (e) {
      toast.error(`Send failed: ${String(e?.message || e).slice(0, 120)}`);
    } finally {
      setSending(false);
    }
  };

  // Iter 210.14 — `variant` toggles the outer chrome. The Lead Focus
  // Email tab expects its own bordered card ('lead-focus'); the dialer
  // Step 4 is already rendered inside a section with its own border,
  // so 'inline-dialer' drops the card and tightens padding.
  const wrapperStyle = variant === "inline-dialer"
    ? { padding: 12, background: "#fff", border: "0.5px solid var(--border)", borderRadius: 10 }
    : { padding: 16, border: "0.5px solid var(--border)", borderRadius: 12, marginBottom: 14, background: "var(--paper)" };

  return (
    <div data-testid="email-compose-form" style={wrapperStyle}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 10, gap: 6 }}>
        <span style={{ fontFamily: "var(--font-head)", fontSize: 13, fontWeight: 600, color: "var(--navy)" }}>
          {replyTo ? "Reply via Outlook" : "Compose email via Outlook"}
        </span>
        {replyTo && (
          <select
            data-testid="email-ai-tone"
            value={tone}
            onChange={(e) => { setTone(e.target.value); aiDraft(e.target.value); }}
            disabled={aiBusy || sending}
            style={{ marginLeft: 8, fontSize: 10, padding: "2px 6px", border: "0.5px solid var(--border)", borderRadius: 5, background: "var(--paper)", color: "var(--text-2)", cursor: "pointer" }}
            title="Re-draft with this tone"
          >
            <option value="warm">Warm</option>
            <option value="formal">Formal</option>
            <option value="concise">Concise</option>
          </select>
        )}
        <button data-testid="email-ai-draft" onClick={() => aiDraft()} disabled={aiBusy || sending} className="btn btn-accent" style={{ marginLeft: "auto", padding: "5px 10px", fontSize: 11 }}>
          <Sparkles style={{ width: 11, height: 11 }} /> {aiBusy ? "Drafting…" : (replyTo ? "Re-draft" : "AI draft")}
        </button>
        <button onClick={onClose} disabled={sending} style={{ marginLeft: 6, background: "none", border: "none", color: "var(--text-3)", cursor: "pointer" }}><X style={{ width: 14, height: 14 }} /></button>
      </div>
      {replyTo && aiAutoTriggered && (
        <div data-testid="email-ai-hint" style={{ fontSize: 10, color: "var(--text-3)", marginBottom: 8, padding: "4px 8px", background: "var(--blue-soft)", border: "0.5px solid #cfe2ff", borderRadius: 6, display: "inline-flex", alignItems: "center", gap: 4 }}>
          <Sparkles style={{ width: 10, height: 10, color: "var(--blue)" }} />
          AI-drafted from the original email. Review, edit, then send.
        </div>
      )}
      {!replyTo && (
        <div style={{ marginBottom: 10 }}>
          <div className="eyebrow" style={{ marginBottom: 4 }}>Template</div>
          <select data-testid="email-template-select" value={tplId} onChange={(e) => applyEmailTemplate(e.target.value)} className="select">
            <option value="">— Pick a template —</option>
            <optgroup label="Built-in">
              {EMAIL_TEMPLATES.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </optgroup>
            {userTemplates.length > 0 && (
              <optgroup label="My published templates">
                {userTemplates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </optgroup>
            )}
          </select>
        </div>
      )}
      <div style={{ marginBottom: 10 }}>
        <div className="eyebrow" style={{ marginBottom: 4 }}>To{replyTo ? "" : " · Subject"}</div>
        <input data-testid="email-to" value={to} onChange={(e) => setTo(e.target.value)} className="input" placeholder="recipient@example.com (comma-separate for multiple)" style={{ marginBottom: 6 }} />
        {!replyTo && (
          <input data-testid="email-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="input" placeholder="Subject" />
        )}
      </div>
      <textarea
        data-testid="email-body"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        className="input"
        placeholder={aiBusy ? "AI drafting reply…" : "Email body…"}
        disabled={aiBusy}
        ref={(el) => { if (!el) return; el.style.height = "auto"; el.style.height = Math.max(el.scrollHeight, 120) + "px"; }}
        style={{ resize: "vertical", fontSize: 12, opacity: aiBusy ? 0.6 : 1, minHeight: 120, overflow: "hidden" }}
      />
      {/* Wave 2 / B8 — show chips for attached Resources (CRM library). */}
      <AttachedResourcesRow
        files={resourceFiles}
        videos={[]}
        onRemoveFile={(i) => setResourceFiles((prev) => prev.filter((_, idx) => idx !== i))}
      />
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 10, alignItems: "center", flexWrap: "wrap" }}>
        {/* Iter 174.8 (Danny B8) — Resources/file attachment picker. */}
        <input
          id="cep-email-attach-input"
          type="file"
          multiple
          data-testid="email-attach-input"
          onChange={(e) => onAttachFiles(e.target.files)}
          style={{ display: "none" }}
        />
        <button
          type="button"
          data-testid="email-attach-btn"
          className="btn"
          onClick={() => document.getElementById("cep-email-attach-input")?.click()}
          disabled={sending}
          title="Attach files (PDFs, images, etc.)"
        >
          📎 Attach
        </button>
        {/* Wave 2 / B8 — pick from CRM Resources (parity with Communications). */}
        <button
          type="button"
          data-testid="email-attach-resources-btn"
          className="btn"
          onClick={() => setResourcePickerOpen(true)}
          disabled={sending}
          title="Attach from CRM Resources"
        >
          📚 From Resources
        </button>
        {attachments.length > 0 && (
          <span data-testid="email-attach-summary" style={{ fontSize: 10, color: "var(--text-3)" }}>
            {attachments.length} file{attachments.length === 1 ? "" : "s"}:{" "}
            {attachments.map((a, i) => (
              <span key={i} style={{ marginRight: 6 }}>
                {a.name}{" "}
                <button
                  type="button"
                  onClick={() => setAttachments((prev) => prev.filter((_, j) => j !== i))}
                  data-testid={`email-attach-remove-${i}`}
                  style={{ background: "transparent", border: "none", color: "#b91c1c", cursor: "pointer", padding: 0, fontSize: 10 }}
                  aria-label={`Remove ${a.name}`}
                >×</button>
              </span>
            ))}
          </span>
        )}
        <span style={{ fontSize: 10, color: "var(--text-3)", marginRight: "auto" }}>
          Sends from your connected Outlook · saved to your Sent Items
        </span>
        <button onClick={onClose} disabled={sending} className="btn">Cancel</button>
        <button data-testid="email-send-btn" className="btn btn-primary" disabled={sending || aiBusy} onClick={send}>
          {sending ? <><Loader2 style={{ width: 11, height: 11, animation: "spin 1s linear infinite" }} /> Sending…</> : <><Send style={{ width: 11, height: 11 }} /> {replyTo ? "Reply" : "Send"}</>}
        </button>
      </div>
      {/* Wave 2 / B8 — Resources slide-in picker (parity with ComposeModal). */}
      <ResourcePickerPanel
        open={resourcePickerOpen}
        onClose={() => setResourcePickerOpen(false)}
        onAttachFile={(f) =>
          setResourceFiles((prev) =>
            prev.find((x) => x.id === f.id) ? prev : [...prev, f]
          )
        }
        onInsertVideoLink={(v) => {
          // Inline the video as a clickable link block at the cursor end.
          setBody((b) => `${b ? b + "\n\n" : ""}▶ ${v.title}: ${v.url}`);
        }}
      />
    </div>
  );
}
